import { useEffect, useMemo } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
  Audio,
  AudioFormValidationProperties,
  AudioTypesDDL,
} from 'types/audio';
import { AppDispatch, useAppSelector } from 'app/store';
import { updateSongAudioEffect } from 'app/store/SongList/effects';
import { addAlertAction } from 'app/store/Alerts/slice';
import { getUserGuid } from 'app/store/Auth/selectors';
import { InputField, ChakraSelectField } from 'app/components/Core';

import { AudioFormItem } from '../AudioAddDialog/types';
import { transformAudioTypesToOptions } from '../AudioAddDialog/utils';
import { getDefaultValues, getValidationSchema } from './utils';

interface AudioEditDialogProps {
  open: boolean;
  handleClose: () => void;
  onDelete: (audio: Audio) => void;
  audioFormValidation: AudioFormValidationProperties;
  audioTypes: AudioTypesDDL;
  audio?: Audio;
  dispatch: AppDispatch;
}

const AudioEditDialogComponent = ({
  open,
  handleClose: closeDialog,
  onDelete,
  audioFormValidation,
  audioTypes,
  audio,
  dispatch,
}: AudioEditDialogProps) => {
  const audioTypesOptions = useMemo(
    () => transformAudioTypesToOptions(audioTypes),
    [audioTypes]
  );

  const {
    control,
    formState: { errors, isSubmitting, isValid, isDirty },
    handleSubmit,
    register,
    reset,
  } = useForm<Partial<AudioFormItem>>({
    defaultValues: getDefaultValues(audioTypesOptions, audio),
    mode: 'all',
    resolver: yupResolver(getValidationSchema(audioFormValidation)),
  });

  const handleClose = () => {
    closeDialog();
    reset();
  };

  const handleDelete = () => {
    if (audio) {
      onDelete(audio);
    }
    handleClose();
  };

  const onSubmit = async (data: Partial<AudioFormItem>) => {
    try {
      if (audio) {
        const { type, ...rest } = data;

        await dispatch(
          updateSongAudioEffect({
            audio,
            formData: {
              ...rest,
              type:
                typeof type === 'object' && type !== null
                  ? (type.value as string)
                  : type,
            },
          })
        ).unwrap();
        dispatch(addAlertAction({ message: 'Changes saved' }));
        handleClose();
      }
    } catch (e) {
      // Handle error
    }
  };

  useEffect(() => {
    if (audio) {
      reset(getDefaultValues(audioTypesOptions, audio));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio?.guid]);

  return (
    <Modal isOpen={open} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader data-testid="dialog-title" title="Edit Audio">
          Edit Audio
        </ModalHeader>
        <ModalBody>
          <InputField
            label="Title"
            isInvalid={Boolean(errors.title)}
            errorMessage={errors.title?.message}
            data-testid="audio-title-field"
            {...register('title')}
          />
          <ChakraSelectField
            control={control}
            name="type"
            label="Type"
            options={audioTypesOptions}
            dataTestId="audio-type-field"
          />
          <InputField
            label="Note"
            isInvalid={Boolean(errors?.remark)}
            errorMessage={errors?.remark?.message}
            data-testid="audio-note-field"
            {...register('remark')}
          />
        </ModalBody>
        <ModalFooter>
          <Stack spacing={2} direction="row" width="full">
            <Button onClick={handleDelete} variant="text" mr="auto">
              Delete
            </Button>
            <Button onClick={handleClose} variant="secondary">
              Cancel
            </Button>
            <Button
              isDisabled={isSubmitting || !isValid || !isDirty}
              onClick={handleSubmit(onSubmit)}
              data-testid="AudioEditDialog-save-btn"
            >
              Save
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const AudioEditDialog = ({
  audio,
  audioTypes,
  ...rest
}: AudioEditDialogProps) => {
  const state = useAppSelector((state) => state);
  const userGuid = getUserGuid(state);
  const isAudioCreator = userGuid === audio?.createdBy.guid;
  const { Private, ...audioTypesWithoutPrivate } = audioTypes;

  return (
    <AudioEditDialogComponent
      audio={audio}
      audioTypes={isAudioCreator ? audioTypes : audioTypesWithoutPrivate}
      {...rest}
    />
  );
};
